import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { AppContext } from "../../../context/AppContextProvider";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Upload,
  message,
  Modal,
  Checkbox,
  Radio,
  Typography,
  Space,
  Descriptions,
  Tag,
  Alert,
  Row,
  Col,
  Tooltip,
  Switch,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  SettingOutlined,
  LoadingOutlined,
  UploadOutlined,
  EyeOutlined,
  EditOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  updateAuthDetails,
  getRegions,
  getCities,
  getBusinessType,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  AddonName,
  CompanyStatus,
} from "../../../configs/constants";
import { useNavigate } from "react-router-dom";
import {
  fourDigitNumberRules,
  postalCodeRules,
  saVatNumberRules,
} from "../../../utils/validators";
import {
  getApiErrorMessages,
  handleApiError,
  showAppError,
} from "../../../utils/errorHandler";
import {
  CERTIFICATE_EXTENSIONS,
  DEFAULT_IMAGE_EXTENSIONS,
  applyAttachmentToPayload,
  createAttachmentRemovalPayload,
  getAttachmentAccept,
  isAttachmentExtensionAllowed,
} from "../../../utils/attachmentPayload";

const { Text, Title } = Typography;

const hasSavedZatcaData = (user) =>
  !!user?.business_arabic_name &&
  ["street_name", "building_number", "plot_identification", "district"].every(
    (field) => !!user?.zatca_profile?.[field],
  );

const SectionHeader = ({ title, subtitle, first = false }) => (
  <div style={{ marginTop: first ? 0 : 8, marginBottom: 16 }}>
    <Title level={4} style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
      {title}
    </Title>
    {subtitle ? (
      <Text type="secondary" style={{ fontSize: 13 }}>
        {subtitle}
      </Text>
    ) : null}
  </div>
);

const StoreConfiguration = ({ isOnboarding = false }) => {
  const {
    authUser,
    setAuthUser,
    setFullPageLoading,
    appConfig,
  } = useContext(AppContext);
  const [isEditMode, setIsEditMode] = useState(isOnboarding);

  const [declarationModalVisible, setDeclarationModalVisible] = useState(false);
  const [acceptCheckbox, setAcceptCheckbox] = useState(false);

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(authUser.logo || "");

  const [crCertificateUploading, setCRCertificateUploading] = useState(false);
  const [crCertificateUrl, setCRCertificateUrl] = useState(
    authUser.cr_certificate || "",
  );

  const [vatCertificateUploading, setVATCertificateUploading] = useState(false);
  const [vatCertificateUrl, setVATCertificateUrl] = useState(
    authUser.vat_certificate || "",
  );

  const [hideFormField, setHideFormField] = useState(
    authUser.company_status !== CompanyStatus.Kyc,
  );

  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const [vatExempt, setVatExempt] = useState(!!authUser.is_vat_exempt);

  const [businessTypes, setBusinessTypes] = useState([]);

  const [KYCForm] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const watchedBusinessRegionId = Form.useWatch("business_region_id", KYCForm);

  const zatcaSaved = hasSavedZatcaData(authUser);
  const userHasManuallySetZatca = useRef(false);
  const [zatcaEnabled, setZatcaEnabled] = useState(zatcaSaved);

  const is_zatca_e_invoicing_active = authUser?.active_addons?.some(
    (addon) => addon?.id && addon?.name === AddonName.ZatcaEInvoicing,
  );

  const can_manage_zatca =
    authUser?.can_manage_zatca === true || authUser?.can_manage_zatca === 1;

  const can_show_zatca_fields = !!is_zatca_e_invoicing_active;
  const can_edit_zatca_fields = is_zatca_e_invoicing_active && can_manage_zatca;

  const handleZatcaToggle = (checked) => {
    userHasManuallySetZatca.current = true;
    setZatcaEnabled(checked);
  };

  useEffect(() => {
    if (userHasManuallySetZatca.current) return;
    setZatcaEnabled(zatcaSaved);
  }, [zatcaSaved]);

  const businessRegionId = authUser.business_region?.id;

  const getZatcaField = useCallback(
    (field) => authUser?.zatca_profile?.[field],
    [authUser?.zatca_profile],
  );

  const handleCityRequestOnLoad = useCallback(
    (value) => {
      if (!value) return;
      setCitiesLoading(true);
      getCities(value)
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          setCities(response.data.data.cities);
        })
        .catch((error) => handleApiError(error, t))
        .finally(() => {
          setCitiesLoading(false);
        });
    },
    [t],
  );

  useEffect(() => {
    setHideFormField(authUser.company_status !== CompanyStatus.Kyc);
  }, [authUser.company_status]);

  useEffect(() => {
    getRegions()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setRegions(response.data.data.regions);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    getBusinessType()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBusinessTypes(response.data.data);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    KYCForm.setFieldsValue({
      business_name: authUser.business_name,
      business_type_id: authUser.business_type?.id,
      business_address: authUser.business_address,
      round_off: !!authUser.round_off,
      is_calories: !!authUser.is_calories,
      preferred_contact_time: authUser.preferred_contact_time,
      cr: authUser.cr,
      is_vat_exempt: !!authUser.is_vat_exempt,
      vat: authUser.vat,
      business_city_id: authUser.business_city?.id,
      business_region_id: authUser.business_region?.id,
      business_country: authUser.business_country || "Saudi Arabia",
      business_postal_code: authUser.business_postal_code,
      business_arabic_name: authUser.business_arabic_name,
      street_name: getZatcaField("street_name"),
      building_number: getZatcaField("building_number"),
      plot_identification: getZatcaField("plot_identification"),
      district: getZatcaField("district"),
    });

    setVatExempt(!!authUser.is_vat_exempt);
    setLogoUrl(authUser.logo || "");
    setCRCertificateUrl(authUser.cr_certificate || "");
    setVATCertificateUrl(authUser.vat_certificate || "");
    handleCityRequestOnLoad(businessRegionId);
  }, [
    authUser,
    businessRegionId,
    KYCForm,
    getZatcaField,
    handleCityRequestOnLoad,
  ]);

  function beforeUpload(file) {
    const isAllowedType = isAttachmentExtensionAllowed(
      file.name,
      DEFAULT_IMAGE_EXTENSIONS,
    );
    if (!isAllowedType) showAppError(t("validate_image_file_type"));

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) showAppError(t("validate_image_size"));

    return isAllowedType && isLt2M;
  }

  function beforeUploadCertificate(file) {
    const isAllowedType = isAttachmentExtensionAllowed(
      file.name,
      CERTIFICATE_EXTENSIONS,
    );

    if (!isAllowedType) showAppError(t("validate_image_pdf_file_type"));

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) showAppError(t("validate_image_size_10"));

    return isAllowedType && isLt10M;
  }

  const handleChange = (info, file_name) => {
    if (info.file.status === "uploading" && file_name === "logo") {
      setLogoUploading(true);
      setLogoUrl("");
    } else if (
      info.file.status === "uploading" &&
      file_name === "cr_certificate"
    ) {
      setCRCertificateUploading(true);
      setCRCertificateUrl("");
    } else if (
      info.file.status === "uploading" &&
      file_name === "vat_certificate"
    ) {
      setVATCertificateUploading(true);
      setVATCertificateUrl("");
    }
  };

  const uploadCustomRequest = (data, file_name) => {
    const formData = new FormData();
    applyAttachmentToPayload(formData, file_name, data.file);

    updateAuthDetails(formData)
      .then((response) => {
        if (response.data.success === true) {
          if (file_name === "logo") setLogoUrl(response.data.data.user.logo);
          if (file_name === "cr_certificate")
            setCRCertificateUrl(response.data.data.user.cr_certificate);
          if (file_name === "vat_certificate")
            setVATCertificateUrl(response.data.data.user.vat_certificate);
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        if (file_name === "logo") setLogoUploading(false);
        if (file_name === "cr_certificate") setCRCertificateUploading(false);
        if (file_name === "vat_certificate") setVATCertificateUploading(false);
      });
  };

  const removeLogo = () => {
    Modal.confirm({
      title: t("remove_logo"),
      content: t("confirm_remove_logo"),
      okText: t("remove"),
      okButtonProps: { danger: true },
      cancelText: t("cancel"),
      onOk: () => {
        const previousUrl = logoUrl;
        setLogoUrl("");
        setLogoUploading(true);
        return updateAuthDetails(createAttachmentRemovalPayload("logo"))
          .then((response) => {
            if (response.data && response.data.success === true) {
              setAuthUser(response.data.data.user);
              setLogoUrl(response.data.data.user.logo || "");
              message.success(t("logo_removed"));
            } else {
              setLogoUrl(previousUrl);
              handleApiError(response, t);
            }
          })
          .catch((error) => {
            setLogoUrl(previousUrl);
            handleApiError(error, t);
          })
          .finally(() => setLogoUploading(false));
      },
    });
  };

  const uploadButton = (file_name) => (
    <div>
      {file_name === "logo" &&
        (logoUploading ? <LoadingOutlined /> : <UploadOutlined />)}
      {file_name === "cr_certificate" &&
        (crCertificateUploading ? <LoadingOutlined /> : <UploadOutlined />)}
      {file_name === "vat_certificate" &&
        (vatCertificateUploading ? <LoadingOutlined /> : <UploadOutlined />)}
      <div className="app-mt-8">{t("upload")}</div>
    </div>
  );

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <HomeOutlined /> },
    {
      path: "store-configuration",
      breadcrumbName: t("store_configuration"),
      icon: <SettingOutlined />,
    },
  ];

  const onFinishConfigForm = (formData) => {
    if (!authUser.cr_certificate && !crCertificateUrl) {
      showAppError(t("validate_cr_certificate_required"));
      return;
    }

    const payload = {
      ...formData,
      round_off: !!formData.round_off,
      is_calories: !!formData.is_calories,
      is_vat_exempt: !!formData.is_vat_exempt,
    };

    if (
      !can_show_zatca_fields ||
      !zatcaEnabled ||
      !can_edit_zatca_fields ||
      zatcaSaved
    ) {
      delete payload.street_name;
      delete payload.building_number;
      delete payload.plot_identification;
      delete payload.district;
    }

    setFullPageLoading(true);
    updateAuthDetails(payload)
      .then((response) => {
        if (response.data && response.data.success === true) {
          setAuthUser(response.data.data.user);
          message.success(t("shop_configuration_successfully"));
          if (!isOnboarding) {
            setIsEditMode(false);
          }
        } else {
          const errors = response?.data?.data?.errors || {};
          const fieldErrors = Object.entries(errors)
            .map(([name, fieldError]) => ({
              name,
              errors: getApiErrorMessages(fieldError),
            }))
            .filter(({ errors: messages }) => messages.length);
          if (fieldErrors.length) {
            KYCForm.setFields(fieldErrors);
          }

          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => setFullPageLoading(false));
  };

  const validateFormFields = async () => {
    try {
      await KYCForm.validateFields();

      if (!authUser.cr_certificate && !crCertificateUrl) {
        showAppError(t("validate_cr_certificate_required"));
        return;
      }

      if (!vatExempt && !authUser.vat_certificate && !vatCertificateUrl) {
        showAppError(t("validate_vat_certificate_required"));
        return;
      }

      setAcceptCheckbox(false);
      setDeclarationModalVisible(true);
    } catch (e) {}
  };

  const handleConfirm = () => {
    if (!acceptCheckbox) {
      showAppError(t("kindly_check_declaration"));
      return;
    }
    setDeclarationModalVisible(false);
    KYCForm.submit();
  };

  const handleRegionSelect = (value) => {
    KYCForm.resetFields(["business_city_id"]);
    setCities([]);

    if (!value) {
      setCitiesLoading(false);
      return;
    }

    setCitiesLoading(true);
    getCities(value)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setCities(response.data.data.cities);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setCitiesLoading(false);
      });
  };

  const handleSkipDetails = () => {
    navigate("/home");
    updateAuthDetails()
      .then((response) => {
        if (response.data && response.data.success === true) {
          setAuthUser(response.data.data.user);
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const localizedName = (entity) => {
    if (!entity) return "—";
    return (
      (appConfig.direction === "rtl"
        ? entity.name_ar
        : entity.name_en || entity.name) || "—"
    );
  };

  const businessInfoSection = (
    <>
      <SectionHeader
        first
        title={t("business_information")}
        subtitle={t("business_information_helper")}
      />

      <div className="app-mb-16">
        <div className="ant-col ant-form-item-label">
          <label title={t("business_logo")}>{t("business_logo")}</label>
          <Text type="warning"> ({t("max_upload_size_2mb_warning")})</Text>
        </div>
        <div style={{ position: "relative", display: "inline-block" }}>
          <Upload
            name="logo"
            listType="picture-card"
            accept={getAttachmentAccept(DEFAULT_IMAGE_EXTENSIONS)}
            showUploadList={false}
            beforeUpload={beforeUpload}
            onChange={(info) => handleChange(info, "logo")}
            customRequest={(data) => uploadCustomRequest(data, "logo")}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={t("business_logo")}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              uploadButton("logo")
            )}
          </Upload>
          {logoUrl && !logoUploading && (
            <Tooltip title={t("remove_logo")}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  removeLogo();
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 2,
                  padding: "0",
                }}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item name="business_name" label={t("business_name")}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="business_arabic_name"
            label={t("business_name_arabic")}
          >
            <Input dir="rtl" disabled />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="business_type_id"
        label={t("business_type")}
        rules={[
          { required: true, message: t("validate_business_type_required") },
        ]}
        hasFeedback
      >
        <Select disabled={hideFormField}>
          {businessTypes.map((business) => (
            <Select.Option key={business.id} value={business.id}>
              {appConfig.direction === "ltr" ? business.name : business.name_ar}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {hideFormField && (
        <>
          <Form.Item name="round_off" label={t("allow_round_off")}>
            <Radio.Group buttonStyle="solid" key="allow-round-off">
              <Radio.Button value={true} className="button_yes">
                {t("yes")}
              </Radio.Button>
              <Radio.Button value={false} className="button_no">
                {t("no")}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="is_calories" label={t("is_calories")}>
            <Radio.Group buttonStyle="solid" key="show-calories">
              <Radio.Button value={true} className="button_yes">
                {t("yes")}
              </Radio.Button>
              <Radio.Button value={false} className="button_no">
                {t("no")}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="preferred_contact_time"
            label={t("preferred_contact_time")}
            rules={[
              {
                required: true,
                message: t("validate_preferred_contact_time_required"),
              },
            ]}
          >
            <Radio.Group>
              <Radio value={"09:00 AM - 02:00 PM"}>09:00 AM - 02:00 PM</Radio>
              <Radio value={"02:00 PM - 05:00 PM"}>02:00 PM - 05:00 PM</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}
    </>
  );

  const taxComplianceSection = (
    <>
      <SectionHeader
        title={t("tax_compliance")}
        subtitle={t("tax_compliance_helper")}
      />

      <Form.Item
        name="cr"
        label={t("cr_number")}
        rules={[
          { required: true, message: t("validate_cr_number_required") },
          { min: 10, message: t("validate_cr_number_exact_limit") },
          { max: 10, message: t("validate_cr_number_exact_limit") },
        ]}
        hasFeedback
      >
        <Input disabled={hideFormField} maxLength={10} inputMode="numeric" />
      </Form.Item>

      <div className="ant-col ant-form-item-label">
        <label className="ant-form-item-required" title={t("cr_certificate")}>
          {t("cr_certificate")}
        </label>
        <Text type="warning"> ({t("max_upload_size_10mb_warning")})</Text>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {!hideFormField && (
          <Upload
            name="cr_certificate"
            listType="picture-card"
            accept={getAttachmentAccept(CERTIFICATE_EXTENSIONS)}
            showUploadList={false}
            beforeUpload={beforeUploadCertificate}
            onChange={(info) => handleChange(info, "cr_certificate")}
            customRequest={(data) =>
              uploadCustomRequest(data, "cr_certificate")
            }
          >
            {crCertificateUrl ? (
              <div>
                <UploadOutlined />
                <div className="app-mt-8">{t("change")}</div>
              </div>
            ) : (
              uploadButton("cr_certificate")
            )}
          </Upload>
        )}
        {crCertificateUrl && (
          <Button type="primary" href={crCertificateUrl} target="_blank">
            <EyeOutlined /> {t("preview") + " " + t("cr_certificate")}
          </Button>
        )}
      </div>

      <Form.Item name="is_vat_exempt" valuePropName="checked">
        <Checkbox
          onChange={(e) => setVatExempt(e.target.checked)}
          disabled={hideFormField}
        >
          {t("is_vat_exempt")}
        </Checkbox>
      </Form.Item>

      {!vatExempt && (
        <Form.Item
          name="vat"
          label={t("vat_number")}
          rules={saVatNumberRules(t, true)}
          hasFeedback
        >
          <Input disabled={hideFormField} maxLength={15} inputMode="numeric" />
        </Form.Item>
      )}

      {!vatExempt && (
        <>
          <div className="ant-col ant-form-item-label">
            <label
              className="ant-form-item-required"
              title={t("vat_certificate")}
            >
              {t("vat_certificate")}
            </label>
            <Text type="warning"> ({t("max_upload_size_10mb_warning")})</Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 16,
            }}
          >
            {!hideFormField && (
              <Upload
                name="vat_certificate"
                listType="picture-card"
                accept={getAttachmentAccept(CERTIFICATE_EXTENSIONS)}
                showUploadList={false}
                beforeUpload={beforeUploadCertificate}
                onChange={(info) => handleChange(info, "vat_certificate")}
                customRequest={(data) =>
                  uploadCustomRequest(data, "vat_certificate")
                }
              >
                {vatCertificateUrl ? (
                  <div>
                    <UploadOutlined />
                    <div className="app-mt-8">{t("change")}</div>
                  </div>
                ) : (
                  uploadButton("vat_certificate")
                )}
              </Upload>
            )}
            {vatCertificateUrl && (
              <Button type="primary" href={vatCertificateUrl} target="_blank">
                <EyeOutlined /> {t("preview") + " " + t("vat_certificate")}
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );

  const zatcaSection = (
    <>
      <SectionHeader
        title={t("zatca_settings")}
        subtitle={t("zatca_settings_subtitle")}
      />
      {!can_manage_zatca && (
        <Alert
          type="warning"
          showIcon
          message={t("permission_required_title")}
          description={t("zatca_permission_required_helper")}
          className="app-mb-16"
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          padding: "12px 16px",
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          marginBottom: zatcaEnabled ? 20 : 0,
        }}
      >
        <Switch
          checked={zatcaEnabled}
          disabled={zatcaSaved || !can_edit_zatca_fields}
          onChange={handleZatcaToggle}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
            {t("zatca_enable_question")}
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {zatcaSaved ? t("zatca_locked_helper") : t("zatca_enable_helper")}
          </Text>
        </div>
      </div>

      {zatcaEnabled && (
        <>
          <Alert
            type="info"
            showIcon
            message={t("zatca_settings_helper_title")}
            description={t("zatca_settings_helper")}
            style={{ marginTop: 20, marginBottom: 20 }}
          />
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="street_name"
                label={t("street_name")}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("validate_street_name_required"),
                  },
                ]}
                hasFeedback
              >
                <Input disabled={zatcaSaved || !can_edit_zatca_fields} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="building_number"
                label={t("building_number")}
                validateFirst
                validateTrigger={["onBlur", "onChange"]}
                rules={fourDigitNumberRules(
                  t,
                  true,
                  "validate_building_number_required",
                  "validate_building_number_4digits",
                )}
                hasFeedback
              >
                <Input
                  maxLength={4}
                  inputMode="numeric"
                  disabled={zatcaSaved || !can_edit_zatca_fields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="plot_identification"
                label={t("plot_identification")}
                validateFirst
                validateTrigger={["onBlur", "onChange"]}
                rules={fourDigitNumberRules(
                  t,
                  true,
                  "validate_plot_identification_required",
                  "validate_plot_identification_4digits",
                )}
                hasFeedback
              >
                <Input
                  maxLength={4}
                  inputMode="numeric"
                  disabled={zatcaSaved || !can_edit_zatca_fields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="district"
                label={t("district")}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("validate_district_required"),
                  },
                ]}
                hasFeedback
              >
                <Input disabled={zatcaSaved || !can_edit_zatca_fields} />
              </Form.Item>
            </Col>
          </Row>

          {!zatcaSaved && (
            <div style={{ marginTop: -8, marginBottom: 16 }}>
              <a
                href="https://address.sa"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--color-warning-hover)", fontSize: 13 }}
              >
                {t("address_sa_help")}
              </a>
            </div>
          )}
        </>
      )}
    </>
  );

  const businessAddressSection = (
    <>
      <SectionHeader
        title={t("business_address_details")}
        subtitle={t("business_address_details_helper")}
      />

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="business_country"
            label={t("country")}
            rules={[
              { required: true, message: t("validate_country_required") },
            ]}
            hasFeedback
          >
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="business_postal_code"
            label={t("postal_code")}
            validateFirst
            validateTrigger={["onBlur", "onChange"]}
            rules={postalCodeRules(t, true)}
            hasFeedback
          >
            <Input maxLength={5} inputMode="numeric" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="business_region_id"
            label={t("region")}
            rules={[{ required: true, message: t("validate_region_required") }]}
            hasFeedback
          >
            <Select
              showSearch
              onChange={handleRegionSelect}
              options={regions.map((region) => ({
                label:
                  appConfig.direction === "ltr"
                    ? region.name_en
                    : region.name_ar,
                value: region.id,
              }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="business_city_id"
            label={t("city")}
            rules={[{ required: true, message: t("validate_city_required") }]}
            hasFeedback
          >
            <Select
              showSearch
              loading={citiesLoading}
              disabled={!watchedBusinessRegionId || citiesLoading}
              options={cities.map((city) => ({
                label:
                  appConfig.direction === "ltr" ? city.name_en : city.name_ar,
                value: city.id,
              }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="business_address"
        label={t("address")}
        rules={[
          {
            required: true,
            message: t("validate_address"),
            whitespace: true,
          },
        ]}
        hasFeedback
      >
        <Input />
      </Form.Item>
    </>
  );

  const submitButton = (
    <Button type="primary" onClick={validateFormFields}>
      {t("submit")}
    </Button>
  );

  const groupCardStyle = {
    marginBottom: 20,
    borderRadius: 12,
    border: "1px solid var(--color-shadow-soft)",
    boxShadow:
      "0 1px 3px var(--color-shadow-soft), 0 8px 24px var(--color-shadow-soft)",
  };

  const formFields = (
    <Form
      name="store-configuration-form"
      layout="vertical"
      onFinish={onFinishConfigForm}
      style={{ width: "100%", margin: "8px auto 0" }}
      form={KYCForm}
      scrollToFirstError
    >
      <Card style={groupCardStyle} bordered={false}>
        {businessInfoSection}
      </Card>

      <Card style={groupCardStyle} bordered={false}>
        {taxComplianceSection}
      </Card>
      {can_show_zatca_fields && (
        <Card style={groupCardStyle} bordered={false}>
          {zatcaSection}
        </Card>
      )}
      <Card style={groupCardStyle} bordered={false}>
        {businessAddressSection}
      </Card>

      <Card style={{ ...groupCardStyle, marginBottom: 0 }} bordered={false}>
        <Form.Item style={{ marginBottom: 0 }}>
          <Space wrap>
            {authUser.is_onboarding_complete !== 1 && (
              <Button type="default" onClick={handleSkipDetails}>
                {t("skip")}
              </Button>
            )}
            {submitButton}
            {!isOnboarding && (
              <Button type="text" danger onClick={() => setIsEditMode(false)}>
                {t("cancel")}
              </Button>
            )}
          </Space>
        </Form.Item>
      </Card>
    </Form>
  );

  const businessInfoView = (
    <Descriptions
      bordered
      column={1}
      size="middle"
      title={t("business_information")}
      labelStyle={{ width: "250px", fontWeight: 500 }}
    >
      <Descriptions.Item label={t("business_name")}>
        {authUser.business_name || "—"}
      </Descriptions.Item>
      <Descriptions.Item label={t("business_name_arabic")}>
        <span dir="rtl">{authUser.business_arabic_name || "—"}</span>
      </Descriptions.Item>
      <Descriptions.Item label={t("business_type")}>
        {localizedName(authUser.business_type)}
      </Descriptions.Item>
      <Descriptions.Item label={t("allow_round_off")}>
        <Tag color={authUser.round_off ? "green" : "red"}>
          {authUser.round_off ? t("yes") : t("no")}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label={t("is_calories")}>
        <Tag color={authUser.is_calories ? "green" : "red"}>
          {authUser.is_calories ? t("yes") : t("no")}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label={t("preferred_contact_time")}>
        {authUser.preferred_contact_time || "—"}
      </Descriptions.Item>
    </Descriptions>
  );

  const taxComplianceView = (
    <Descriptions
      bordered
      column={1}
      size="middle"
      title={t("tax_compliance")}
      labelStyle={{ width: "250px", fontWeight: 500 }}
    >
      <Descriptions.Item label={t("cr_number")}>
        {authUser.cr || "—"}
      </Descriptions.Item>
      <Descriptions.Item label={t("cr_certificate")}>
        {crCertificateUrl ? (
          <Button
            type="link"
            href={crCertificateUrl}
            target="_blank"
            icon={<EyeOutlined />}
            className="app-p-0"
          >
            {t("preview")}
          </Button>
        ) : (
          "—"
        )}
      </Descriptions.Item>

      {!authUser.is_vat_exempt ? (
        <>
          <Descriptions.Item label={t("vat_number")}>
            {authUser.vat || "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("vat_certificate")}>
            {vatCertificateUrl ? (
              <Button
                type="link"
                href={vatCertificateUrl}
                target="_blank"
                icon={<EyeOutlined />}
                className="app-p-0"
              >
                {t("preview")}
              </Button>
            ) : (
              "—"
            )}
          </Descriptions.Item>
        </>
      ) : (
        <Descriptions.Item label={t("is_vat_exempt")}>
          <Tag color="green">{t("yes")}</Tag>
        </Descriptions.Item>
      )}
    </Descriptions>
  );

  const zatcaView = zatcaSaved ? (
    <Descriptions
      bordered
      column={1}
      size="middle"
      title={t("zatca_settings")}
      labelStyle={{ width: "250px", fontWeight: 500 }}
    >
      <Descriptions.Item label={t("street_name")}>
        {getZatcaField("street_name") || "—"}
      </Descriptions.Item>
      <Descriptions.Item label={t("building_number")}>
        {getZatcaField("building_number") || "—"}
      </Descriptions.Item>
      <Descriptions.Item label={t("plot_identification")}>
        {getZatcaField("plot_identification") || "—"}
      </Descriptions.Item>
      <Descriptions.Item label={t("district")}>
        {getZatcaField("district") || "—"}
      </Descriptions.Item>
    </Descriptions>
  ) : (
    <>
      <SectionHeader first title={t("zatca_settings")} />
      <Alert
        type="info"
        showIcon
        message={t("zatca_not_configured")}
        description={t("zatca_not_configured_helper")}
      />
    </>
  );

  const businessAddressView = (
    <Descriptions
      bordered
      column={1}
      size="middle"
      title={t("business_address_details")}
      labelStyle={{ width: "250px", fontWeight: 500 }}
    >
      <Descriptions.Item label={t("country")}>
        {authUser.business_country || t("saudi_arabia")}
      </Descriptions.Item>
      <Descriptions.Item label={t("region")}>
        {localizedName(authUser.business_region)}
      </Descriptions.Item>
      <Descriptions.Item label={t("city")}>
        {localizedName(authUser.business_city)}
      </Descriptions.Item>
      <Descriptions.Item label={t("postal_code")}>
        {authUser.business_postal_code || "—"}
      </Descriptions.Item>
      <Descriptions.Item label={t("address")}>
        {authUser.business_address || "—"}
      </Descriptions.Item>
    </Descriptions>
  );

  const viewMode = (
    <>
      {!isOnboarding && (
        <Card className="app-mb-20">
          <CustomBreadcrumb menus={breadcrumbRoutes} />
          <PageHeader
            className="app-p-0"
            title={t("store_configuration")}
            ghost={false}
            extra={[
              <Button
                key="edit"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsEditMode(true)}
              >
                {t("edit")}
              </Button>,
            ]}
          />
        </Card>
      )}

      <div
        style={{
          direction: appConfig.direction === "rtl" ? "rtl" : "ltr",
        }}
      >
        {logoUrl && (
          <Card style={groupCardStyle} bordered={false}>
            <img
              src={logoUrl}
              alt={t("business_logo")}
              style={{
                width: "100px",
                height: "100px",
                objectFit: "contain",
                border: "1px solid var(--color-bg)",
                borderRadius: "8px",
                padding: "8px",
              }}
            />
          </Card>
        )}

        <Card style={groupCardStyle} bordered={false}>
          {businessInfoView}
        </Card>

        <Card style={groupCardStyle} bordered={false}>
          {taxComplianceView}
        </Card>
        {can_show_zatca_fields && (
          <Card style={groupCardStyle} bordered={false}>
            {zatcaView}
          </Card>
        )}
        <Card style={{ ...groupCardStyle, marginBottom: 0 }} bordered={false}>
          {businessAddressView}
        </Card>
      </div>
    </>
  );

  const editMode = (
    <>
      {!isOnboarding ? (
        <>
          <Card className="app-mb-20">
            <CustomBreadcrumb menus={breadcrumbRoutes} />
            <PageHeader
              className="app-p-0"
              title={t("store_configuration")}
              ghost={false}
              extra={[
                <Button
                  type="text"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => setIsEditMode(false)}
                >
                  {t("cancel")}
                </Button>,
              ]}
            />
          </Card>

          <div
            style={{
              direction: appConfig.direction === "rtl" ? "rtl" : "ltr",
            }}
          >
            {formFields}
          </div>
        </>
      ) : (
        <div
          style={{
            minHeight: "100vh",
            background: "var(--color-primary-soft)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 24px 80px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "720px",
              direction: appConfig.direction === "rtl" ? "rtl" : "ltr",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <h2
                style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  margin: "0 0 8px",
                  color: "var(--color-text)",
                }}
              >
                {t("store_configuration")}
              </h2>
              <p
                style={{
                  color: "var(--color-text)",
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {t("details_activate_company")}
              </p>
            </div>

            {formFields}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {isEditMode ? editMode : viewMode}

      <Modal
        title={t("declaration")}
        open={declarationModalVisible}
        onCancel={() => setDeclarationModalVisible(false)}
        onOk={handleConfirm}
      >
        <Checkbox
          onChange={(e) => setAcceptCheckbox(e.target.checked)}
          checked={acceptCheckbox}
        >
          {t("declaration_text")}
        </Checkbox>
      </Modal>
    </>
  );
};

export default StoreConfiguration;
